import { RadioGroup, RadioItem } from '@/src/components/Antd';
import CommonHeader from '@/src/components/CommonHeader';
import Configuration from '@/src/components/Configuration/Configuration';
import NoPermission from '@/src/components/NoPermission';
import PasswordInput from '@/src/components/Password';
import { getSalesChannel } from '@/src/request/salesChannel/salesChannel';
import {
  getDigitalMarketing,
  postDigitalMarketing,
} from '@/src/request/salesChannel/setting/digitalMarketing';
import {
  isUserValid,
  salesChannelResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { useRequest } from 'ahooks';
import { Checkbox, Form, Input, Spin } from 'antd';

import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet, replaceI18nVariables } from 'src/tools/tools';
import { ChannelType } from 'src/types/common/SalesChannel';
import MetaTag from './MetaTag';
import styles from './style.module.less';
import Script from './Script';
import { msg } from 'src/tools/message';
import { get } from 'lodash';

const permissions = (uuid: string) => {
  const view_digital_marketing_settings = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelDigitalMarketing,
        operation: EnumOperation.view_digital_marketing_settings,
        uuid,
      }),
    ],
  });
  const edit_digital_marketing_settings = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelDigitalMarketing,
        operation: EnumOperation.edit_digital_marketing_settings,
        uuid,
      }),
    ],
  });

  return {
    view_digital_marketing_settings,
    edit_digital_marketing_settings,
  };
};

const DigitalMarketing = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const params: any = useParams();
  const { uuid } = params;
  const { view_digital_marketing_settings, edit_digital_marketing_settings } =
    permissions(uuid);
  const [form] = Form.useForm();
  const [unlockEditable, setUnlockEditable] = useState<any[]>([]);
  const [enableGoogleAnalytics, setEnableGoogleAnalytics] = useState(false);
  const [onSaveDisable, setOnSaveDisable] = useState<boolean>(true);
  const [enableGoogleTagManager, setEnableGoogleTagManager] = useState(false);
  const [radioValue, setRadioValue] = useState('GA3');
  const [salesChannelType, setSalesChannelType] = useState('');
  const [salesChannelName, setSalesChannelName] = useState('');
  const bckupList = React.useRef<null>(null);
  const isMini = salesChannelType === ChannelType.MiniProgram;

  const { loading } = useRequest(() => getDigitalMarketing(uuid), {
    onSuccess: (data) => {
      const {
        googleAnalytics = {},
        googleTagManager = {},
        metaTagList,
        script,
      } = data || {};
      const defaultForm = {
        googleAnalyticsEnabled: googleAnalytics.enabled,
        googleAnalyticsType: googleAnalytics.type,
        universalTrackingId: googleAnalytics.universalTrackingId,
        measurementId: googleAnalytics.measurementId,
        googleTagManagerEnabled: googleTagManager.enabled,
        containerId: googleTagManager.containerId,
        apiSecret: googleAnalytics.apiSecret,
        metaTagList,
        script: script,
      };
      if (!googleAnalytics.apiSecret) {
        setUnlockEditable(['apiSecret']);
      }
      setEnableGoogleAnalytics(googleAnalytics.enabled);
      setEnableGoogleTagManager(googleTagManager.enabled);
      setRadioValue(googleAnalytics.type);
      form.setFieldsValue(defaultForm);
    },
  });

  const { run: _postDigitalMarketing } = useRequest(
    (submitData) => postDigitalMarketing(uuid, submitData),
    {
      manual: true,
      onSuccess: () => {
        msg.success(getI18n('general.successfullyupdated'));

        history.push(`/salesChannel/settings/${uuid}`);
      },
    },
  );
  const onValuesChange = (changedValues, allValues) => {
    if (!get(changedValues, 'script.enableJavascript')) {
      const curV = get(allValues, 'script.scriptList');
      bckupList.current = curV;
    }
    if (onSaveDisable) {
      setOnSaveDisable(!onSaveDisable);
    }
  };
  const onFormSubmit = () => {
    let submitData = {};
    form.validateFields().then((res) => {
      const sc = get(res, 'script.enableJavascript')
        ? res.script
        : {
            ...(res.script || {}),
            scriptList: bckupList.current,
          };
      submitData = {
        googleAnalytics: {
          enabled: res.googleAnalyticsEnabled,
          type: isMini ? 'GA4' : res.googleAnalyticsType,
          universalTrackingId: res.universalTrackingId,
          measurementId: res.measurementId,
          apiSecret: res.apiSecret,
        },
        googleTagManager: {
          enabled: res.googleTagManagerEnabled,
          containerId: res.containerId,
        },
        metaTagList: res.metaTagList || [],
        script: sc,
      };
      _postDigitalMarketing(submitData);
    });
  };

  const handleResetInput = (key) => {
    form.setFieldsValue({ [key]: '' });
    const unlockEditableCopy = cloneDeep(unlockEditable);
    unlockEditableCopy.push(key);
    setUnlockEditable(unlockEditableCopy);
  };

  useEffect(() => {
    if (view_digital_marketing_settings) {
      getSalesChannel(uuid).then((data: any) => {
        setSalesChannelName(data.name);
        setSalesChannelType(data.channelType);
      });
    }
  }, []);
  const history = useHistory();
  const goBackToList = () => {
    history.push(`/salesChannel/settings/${uuid}`);
  };
  const breadcrumbData = [
    {
      url: '/',
      name: getI18n('general.sider.home'),
    },
    {
      url: '/salesChannels',
      name: getI18n('general.sider.salesChannel'),
    },
    {
      url: '',
      name: salesChannelName,
    },
    {
      url: `/salesChannel/settings/${uuid}`,
      name: getI18n('general.sider.settings'),
    },
    {
      url: '',
      name: getI18n('salesChannelDigitalMarketing.index.digitalMarketing'),
    },
  ];

  return view_digital_marketing_settings ? (
    <div className={styles.container}>
      <Spin spinning={loading}>
        <Form
          layout={'vertical'}
          form={form}
          className={styles.editForm}
          onValuesChange={onValuesChange}>
          <CommonHeader
            breadcrumbData={breadcrumbData}
            onSaveDisable={onSaveDisable || !edit_digital_marketing_settings}
            onSubmit={onFormSubmit}
            onDiscard={goBackToList}
            onGoBackClick={goBackToList}
          />
          <div className={styles.itemBlock}>
            <div className={styles.header}>
              {getI18n('salesChannelDigitalMarketing.index.googleAnalytics')}
            </div>
            <div className={styles.content}>
              <Form.Item
                name={'googleAnalyticsEnabled'}
                valuePropName='checked'>
                <Checkbox
                  onChange={(e) => {
                    setEnableGoogleAnalytics(e.target.checked);
                  }}
                  disabled={!edit_digital_marketing_settings}>
                  <span>
                    {getI18n(
                      'salesChannelDigitalMarketing.index.enableGoogleAnalytics',
                    )}
                  </span>
                </Checkbox>
              </Form.Item>
              {enableGoogleAnalytics && (
                <div>
                  {!isMini && (
                    <div className={styles.item}>
                      <div className={styles.inputTitle}>
                        {getI18n('general.type')}
                      </div>
                      <div>
                        <Form.Item name={'googleAnalyticsType'}>
                          <RadioGroup
                            value={radioValue}
                            className={styles.radioGroup}
                            onChange={(e) => {
                              setRadioValue(e.target.value);
                            }}
                            disabled={!edit_digital_marketing_settings}>
                            {/* <RadioItem
                              disabled={false}
                              className={styles.radioItem}
                              value={'GA3'}>
                              GA3
                            </RadioItem> */}
                            <RadioItem
                              className={styles.radioItem}
                              value={'GA4'}>
                              GA4
                            </RadioItem>
                          </RadioGroup>
                        </Form.Item>
                      </div>
                    </div>
                  )}
                  <div className={styles.containItem}>
                    <div className={styles.item}>
                      <div className={styles.inputTitle}>
                        {radioValue == 'GA3' && !isMini
                          ? 'GA3 Universal Tracking ID'
                          : 'GA4 Measurement ID'}
                      </div>
                      <div className={styles.input}>
                        <Form.Item
                          normalize={(v) => v?.trim()}
                          rules={[
                            {
                              required: true,
                              message: replaceI18nVariables(
                                getI18n('general.fieldisrequired'),
                                radioValue == 'GA3'
                                  ? 'Universal Tracking ID'
                                  : 'Measurement ID',
                              ),
                            },
                          ]}
                          name={
                            radioValue == 'GA3'
                              ? 'universalTrackingId'
                              : 'measurementId'
                          }>
                          <Input
                            placeholder={
                              radioValue == 'GA3' ? 'UA-000000-0' : 'G-XXXXXXX'
                            }
                            size='large'
                            disabled={!edit_digital_marketing_settings}
                          />
                        </Form.Item>
                      </div>
                    </div>
                    {isMini && (
                      <div className={styles.item}>
                        <div className={styles.inputTitle}>
                          {`Measurement Protocol API ${getI18n(
                            'salesChannelDigitalMarketing.index.secretValue',
                          )}`}
                          *
                        </div>
                        <div className={styles.input}>
                          <Form.Item
                            rules={[
                              {
                                required: true,
                                message: replaceI18nVariables(
                                  getI18n('general.fieldisrequired'),
                                  `Measurement Protocol API ${getI18n(
                                    'salesChannelDigitalMarketing.index.secretValue',
                                  )}`,
                                ),
                              },
                            ]}
                            name={'apiSecret'}>
                            <PasswordInput
                              editable={unlockEditable.includes('apiSecret')}
                              onEdit={() => {
                                handleResetInput('apiSecret');
                              }}
                              passwordProps={{ maxLength: 100 }}
                              disabled={!edit_digital_marketing_settings}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    )}
                  </div>
                  {isMini && (
                    <div className={styles.miniTip}>
                      <Configuration
                        href='https://mp.weixin.qq.com/'
                        configStart={replaceI18nVariables(
                          getI18n(
                            'salesChannelDigitalMarketing.index.miniProgramPortalStart',
                          ),
                          'https://google-analytics.com',
                          '”request 合法域名”',
                        )}
                        configLink={`${getI18n(
                          'salesChannelDigitalMarketing.index.miniProgramPortal',
                        )} `}
                        configEnd=' > 开发设置 > 服务器域名 > 修改.​'
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {!isMini && (
            <div className={styles.itemBlock}>
              <div className={styles.header}>
                {getI18n('salesChannelDigitalMarketing.index.googleTagManager')}
              </div>
              <div className={styles.content}>
                <Form.Item
                  name={'googleTagManagerEnabled'}
                  valuePropName='checked'>
                  <Checkbox
                    onChange={(e) => {
                      setEnableGoogleTagManager(e.target.checked);
                    }}
                    disabled={!edit_digital_marketing_settings}>
                    <span>
                      {getI18n(
                        'salesChannelDigitalMarketing.index.enableGoogleTagManager',
                      )}
                    </span>
                  </Checkbox>
                </Form.Item>
                {enableGoogleTagManager && (
                  <div>
                    <div className={styles.item}>
                      <div className={styles.inputTitle}>
                        {getI18n(
                          'salesChannelDigitalMarketing.index.containerID',
                        )}
                      </div>
                      <div className={styles.input}>
                        <Form.Item
                          name={'containerId'}
                          normalize={(v) => v?.trim()}
                          rules={[
                            {
                              required: true,
                              message: replaceI18nVariables(
                                getI18n('general.fieldisrequired'),
                                getI18n(
                                  'salesChannelDigitalMarketing.index.containerID',
                                ),
                              ),
                            },
                          ]}>
                          <Input
                            size='large'
                            placeholder='GTM-XXXXXX'
                            disabled={!edit_digital_marketing_settings}
                          />
                        </Form.Item>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {!isMini && <MetaTag form={form} />}

          <Script form={form} onValuesChange={onValuesChange} />
        </Form>
      </Spin>
    </div>
  ) : (
    <NoPermission />
  );
};
export default DigitalMarketing;
